import { FlashBagType } from '@enums/flashbag';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import moment from 'moment/moment';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import eventBus from '@tools/event-bus';
import Logger from '@tools/logger/logger';
import momentMixin from '@tools/moment';
import requests from '@tools/requests';
import {
  URL_ADAF_DEAL_EXTEND,
  URL_ADAF_DEALPAYMENT_UPDATE,
  URL_ADAF_MONTH,
  URL_SUBSCRIPTION_CREATE,
  URL_SUBSCRIPTION_EDIT,
} from '@tools/routes/routes';
import {
  getCompleteShorterReadableDate, isDateInPast,
  isDateInThisMonth,
  isTodayBeforeDayOfMonth,
} from '@tools/date';
import translation, { trans, transchoice } from '../../../_00_elements/translation';
import { monthAndYear } from '../../adaf/mixins';
import { EVENT_SLIDERS } from '../general/event-constants';
import useInvoiceModalStore from './invoiceModal';
import { currency } from '../../../_00_elements/number-formatter';
import { STRIPE_STATUS, SUBSCRIPTION_STATUS } from '../constants/constants';

const useAdafStore = defineStore('adaf', () => {
  const { setModal } = useInvoiceModalStore();
  const selectedDealPayment = ref(null);
  const lines = ref([]);
  const linesMonthBefore = ref([]);
  const salesRevenues = ref(0);
  const salesRevenuesMonthBefore = ref(0);
  const sumRecurringFee = ref(0);
  const sumRecurringFeeMonthBefore = ref(0);
  const sumOneShotFee = ref(0);
  const sumOneShotFeeMonthBefore = ref(0);
  const userSetting = ref({});
  const accountManager = ref({});
  const displayDate = ref(new Date());
  const deadlineDay = ref(1);
  const loading = ref(false);
  const ctaLoading = ref(false);
  const loadingFilter = ref(false);
  const addressesSelectedOnFilter = ref([]);

  const headersTable = [
    { key: 'enterprise', title: trans('gestion.adaf.table.enterprise') },
    { key: 'amount_dealpayment', title: trans('gestion.adaf.table.amount') },
    {
      key: 'active_subscription',
      title: trans('gestion.adaf.table.subscription'),
      transPrefix: 'gestion.subscription_deal_payment.status.',
    },
    { key: 'period', title: trans('gestion.adaf.table.period') },
    {
      key: 'commission_type',
      title: trans('gestion.adaf.table.commission_type'),
      transPrefix: 'gestion.commission_type.',
    },
    { key: 'space', title: trans('gestion.adaf.table.space') },
    { key: 'offices', title: trans('gestion.adaf.table.offices') },
    { key: 'end_date', title: trans('gestion.adaf.table.end_date') },
    { key: 'end_commission', title: trans('gestion.adaf.table.end_commission') },
    { key: 'action', title: '', actions: [ { text: '', title: '' } ] },
  ];

  const setData = (data) => {
    lines.value = data.lines;
    salesRevenues.value = Number(data.sumPrice);
    sumRecurringFee.value = Number(data.sumRecurringFee);
    sumOneShotFee.value = Number(data.sumOneShotFee);
    userSetting.value = data.user_settings;
    /**
     * Warning! We use unix instead of iso because iso creates a bug on old safari versions
     */
    displayDate.value = new Date(data.month.unix * 1000);
    accountManager.value = data.account_manager;
    deadlineDay.value = data.settings.adaf_deadline;
  };

  const setMonthBefore = (data) => {
    linesMonthBefore.value = data.lines;
    salesRevenuesMonthBefore.value = Number(data.sumPrice);
    sumRecurringFeeMonthBefore.value = Number(data.sumRecurringFee);
    sumOneShotFeeMonthBefore.value = Number(data.sumOneShotFee);
  };

  const setSelectedDealPayment = (value) => {
    selectedDealPayment.value = value;
  };

  const differenceTenant = computed(() => {
    if (!linesMonthBefore.value || linesMonthBefore.value.length === 0) {
      return lines.value.length * 100;
    }
    return Math.round(((lines.value.length - linesMonthBefore.value.length) / linesMonthBefore.value.length) * 100);
  });

  const differenceSalesRevenues = computed(() => {
    if (salesRevenuesMonthBefore.value === 0) {
      return salesRevenues.value;
    }
    return Math.round(((salesRevenues.value - salesRevenuesMonthBefore.value) / salesRevenuesMonthBefore.value) * 100);
  });

  const fetchPayments = async ({ month, addressIds, addressIdsString, resultButton = false }) => {
    loading.value = !resultButton;
    loadingFilter.value = resultButton;

    const routeUrl = URL_ADAF_MONTH.replace('{month}', month || moment().format('YYYYMM'));
    const queryParams = new URLSearchParams();

    if (addressIds !== '') queryParams.append('addresses', addressIdsString);

    const response = await requests.get(`${routeUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);

    loading.value = false;
    loadingFilter.value = false;

    if (response.status !== 200) {
      flashBagError();
      Logger.error('Error catched', response);
      return;
    }

    if (!resultButton) {
      setData(response.data);
      setMonthBefore(response.data.previous_month);
    } else {
      addressesSelectedOnFilter.value = addressIds.split(',');
    }
  };

  const displaySpaceLabel = (space) => ((space.name === '') ? `${space.number} ${space.street}` : space.name);
  const displayOfficesLabel = (offices) => offices.map((office) => office.office_offer.name).join(' + ');

  const openEditAmountSlider = (dealPayment) => {
    setSelectedDealPayment(dealPayment);
    setModal({
      modalTitle: transchoice('gestion.adaf.action.edit_amount', { date: momentMixin.methods.getHumanMonth() }),
      modalComponentName: EVENT_SLIDERS.EDIT_AMOUNT_ADAF,
      shouldOpenModal: true,
    });
  };

  const openEditSubscriptionSlider = (dealPayment) => {
    setSelectedDealPayment(dealPayment);

    setModal({
      modalTitle: trans('gestion.adaf.action.edit_subscription'),
      modalComponentName: EVENT_SLIDERS.EDIT_SUBSCRIPTION_ADAF,
      shouldOpenModal: true,
    });
  };

  const dealPaymentsActions = (dealPayment) => {
    if (dealPayment.deal?.subscription) {
      const transKey = (dealPayment.deal.subscription.subscription_status.toLowerCase() === SUBSCRIPTION_STATUS.TERMINATED)
        ? trans('gestion.adaf.action.see_subscription')
        : trans('gestion.adaf.action.manage_subscription');

      return [
        {
          hidden: true,
          text: transKey,
          href: URL_SUBSCRIPTION_EDIT.replace('{id}', dealPayment.deal.subscription.id),
          target: '_blank',
        },
      ];
    }

    const actions = [
      {
        hidden: true,
        text: transchoice('gestion.adaf.action.edit_amount', { date: momentMixin.methods.getHumanMonth() }),
        click: () => openEditAmountSlider(dealPayment),
        customClass: 'dropdown-border-top',
        isDisplayed: isDateInThisMonth(displayDate.value) && isTodayBeforeDayOfMonth(deadlineDay.value),
      },
      {
        hidden: true,
        text: trans('gestion.adaf.action.edit_subscription'),
        click: () => openEditSubscriptionSlider(dealPayment),
        customClass: 'dropdown-border-top',
        isDisplayed: isDateInThisMonth(displayDate.value),
      },
    ];

    if (dealPayment.deal?.subscription && dealPayment.deal.subscription.subscription_status !== STRIPE_STATUS.ACTIVE) {
      actions.unshift(
        {
          hidden: true,
          text: trans('gestion.adaf.action.see_subscription'),
          href: URL_SUBSCRIPTION_EDIT.replace('{id}', dealPayment.deal.subscription.id),
          target: '_blank',
          isDisplayed: true,
        },
      );
      return actions.filter((action) => action.isDisplayed);
    }

    actions.unshift(
      {
        hidden: true,
        text: trans('gestion.adaf.action.create_subscription'),
        href: `${URL_SUBSCRIPTION_CREATE}?deal=${dealPayment.deal.id}`,
        target: '_blank',
        customClass: 'dropdown-bold',
        isDisplayed: isDateInThisMonth(displayDate.value) || (dealPayment.deal.end_date === null || isDateInPast(dealPayment.deal.end_date)),
      },
    );

    return actions.filter((action) => action.isDisplayed);
  };

  const postRequest = async (url, parameters) => {
    try {
      ctaLoading.value = true;
      const response = await requests.post(url, { ...parameters });

      if (response.status === 200 || response.status === 201) {
        flashBagSuccess(trans('gestion.adaf.flashbag.success'));
        setData(response.data);
        setMonthBefore(response.data.previous_month);

        return true;
      }
      flashBagError(response.message);
    } catch (error) {
      flashBagError(error.response.data.error);
    } finally {
      ctaLoading.value = false;
    }

    return false;
  };

  const editAmount = async (price) => postRequest(
    URL_ADAF_DEALPAYMENT_UPDATE.replace('{id}', selectedDealPayment.value.id),
    { price },
  );

  const editSubscription = async (price, endDate, isUnlimited) => postRequest(
    URL_ADAF_DEAL_EXTEND.replace('{id}', selectedDealPayment.value.deal.id),
    {
      price,
      endDate: endDate ?? null,
      unlimited: isUnlimited,
    },
  );

  const getCommissionEndDate = (deal) => {
    const startDate = new Date(deal.start_date.unix * 1000);

    const startDatePlusOneYear = new Date(startDate.getTime());
    startDatePlusOneYear.setFullYear(startDatePlusOneYear.getFullYear() + 1);

    const endDate = deal.end_date ? new Date(deal.end_date.unix * 1000) : null;
    if (endDate && endDate < startDatePlusOneYear) {
      return getCompleteShorterReadableDate(endDate);
    }

    return getCompleteShorterReadableDate(startDatePlusOneYear);
  };

  const getSubscriptionStatus = (dealPayment) => {
    if (!dealPayment.deal?.subscription) {
      return SUBSCRIPTION_STATUS.INACTIVE;
    }

    return dealPayment.deal.subscription.subscription_status === STRIPE_STATUS.ACTIVE
      ? SUBSCRIPTION_STATUS.ACTIVE
      : SUBSCRIPTION_STATUS.TERMINATED;
  };

  const formattedDealPaymentLines = computed(() => lines.value
    .flatMap((dealPaymentsBuyer) => dealPaymentsBuyer.map((dealPayment) => ({
      id: dealPayment.id,
      enterprise: dealPayment.deal.buyer_orga_name ? dealPayment.deal.buyer_orga_name : dealPayment.deal.buyer.full_name,
      amount_dealpayment: currency(dealPayment.price),
      active_subscription: getSubscriptionStatus(dealPayment),
      period: monthAndYear(dealPayment.month),
      commission_type: dealPayment.payment_mode ?? 'none',
      space: displaySpaceLabel(dealPayment.deal.first_office_offer.address),
      offices: displayOfficesLabel(dealPayment.deal.office_offers_collection),
      end_date: dealPayment.deal.end_date ? getCompleteShorterReadableDate(dealPayment.deal.end_date.iso) : '-',
      end_commission: getCommissionEndDate(dealPayment.deal),
      actions: dealPaymentsActions(dealPayment),
    }))));

  const dealPaymentsForMobile = computed(() => lines.value.flatMap((dealPaymentsBuyer) => dealPaymentsBuyer.map((dealPayment) => ({
    id: dealPayment.id,
    name: dealPayment.deal.buyer_orga_name ? dealPayment.deal.buyer_orga_name : dealPayment.deal.buyer.full_name,
    amount: currency(dealPayment.price),
    period: monthAndYear(dealPayment.month),
    commission_type: dealPayment.payment_mode ?? 'none',
    actions: dealPaymentsActions(dealPayment),
  }))));

  return {
    accountManager,
    addressesSelectedOnFilter,
    ctaLoading,
    dealPaymentsForMobile,
    deadlineDay,
    differenceSalesRevenues,
    differenceTenant,
    displayDate,
    displaySpaceLabel,
    displayOfficesLabel,
    fetchPayments,
    formattedDealPaymentLines,
    headersTable,
    selectedDealPayment,
    setSelectedDealPayment,
    editAmount,
    editSubscription,
    lines,
    linesMonthBefore,
    loading,
    loadingFilter,
    salesRevenues,
    salesRevenuesMonthBefore,
    setData,
    setMonthBefore,
    sumOneShotFee,
    sumOneShotFeeMonthBefore,
    sumRecurringFee,
    sumRecurringFeeMonthBefore,
    userSetting,
  };
});

export default useAdafStore;
